import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { Card, Btn, Modal, Table, Spinner, ErrBox } from '../../../components/UI';
import Badge from '../../../components/Badge';
import { assetsAPI } from '../../../utils/api';

export default function AssetCategoryTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    code: '', name: '', description: '', economic_life_months: '', depreciation_method: 'straight-line', residual_value_pct: 0
  });

  const inputStyle = { padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

  const loadCategories = async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await assetsAPI.getCategories();
      setCategories(r.data || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadCategories(); }, []);

  const handleSave = async () => {
    if (!form.code || !form.name || !form.economic_life_months)
      return alert('Kode, Nama, dan Umur Ekonomis wajib diisi');
    setSaving(true);
    try {
      if (editId) {
        await assetsAPI.updateCategory(editId, form);
      } else {
        await assetsAPI.createCategory(form);
      }
      setShowModal(false);
      setEditId(null);
      setForm({ code: '', name: '', description: '', economic_life_months: '', depreciation_method: 'straight-line', residual_value_pct: 0 });
      loadCategories();
    } catch (e) { alert("Gagal menyimpan: " + e.message); }
    finally { setSaving(false); }
  };

  const handleEdit = (cat) => {
    setEditId(cat.id);
    setForm({
      code: cat.code,
      name: cat.name,
      description: cat.description || '',
      economic_life_months: cat.economic_life_months || '',
      depreciation_method: cat.depreciation_method || 'straight-line',
      residual_value_pct: cat.residual_value_pct || 0
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    setForm({ code: '', name: '', description: '', economic_life_months: '', depreciation_method: 'straight-line', residual_value_pct: 0 });
  };

  const depMethods = [
    { value: 'straight-line', label: 'Garis Lurus' },
    { value: 'declining-balance', label: 'Saldo Menurun' },
    { value: 'sum-of-years', label: 'Jumlah Angka Tahun' },
    { value: 'units-of-production', label: 'Unit Produksi' },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>Daftar Kategori Aset ({categories.length})</h3>
          <Btn icon={<Plus size={14}/>} onClick={() => setShowModal(true)}>Tambah Kategori</Btn>
        </div>
        {err && <div style={{marginBottom: 12}}><ErrBox msg={err} onRetry={loadCategories}/></div>}
        {loading ? <Spinner/> : (
          <Table pageSize={10} cols={[
            { key: "code", label: "Kode", nowrap: true },
            { key: "name", label: "Nama Kategori" },
            { key: "description", label: "Deskripsi" },
            { key: "economic_life_months", label: "Umur (bln)", align: "right" },
            { key: "depreciation_method", label: "Metode Depresiasi" },
            { key: "is_active", label: "Status", render: v => (
              <span style={{
                padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                background: v ? '#ECFDF5' : '#FEF2F2', color: v ? '#047857' : '#DC2626'
              }}>
                {v ? 'Aktif' : 'Nonaktif'}
              </span>
            )},
            { key: "id", label: "Aksi", render: (id, row) => (
              <div style={{ display: "flex", gap: 6 }}>
                <Btn size="sm" variant="secondary" onClick={() => handleEdit(row)}>Edit</Btn>
              </div>
            )},
          ]} data={categories}/>
        )}
      </Card>

      {showModal && (
        <Modal title={editId ? "Edit Kategori Aset" : "Tambah Kategori Aset"} onClose={closeModal}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Kode *</label>
              <input value={form.code} onChange={e => setForm({...form, code: e.target.value})}
                placeholder="Contoh: ELEKTRONIK"
                style={inputStyle} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Nama Kategori *</label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                placeholder="Contoh: Peralatan Elektronik"
                style={inputStyle} />
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Deskripsi</label>
            <input value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              placeholder="Deskripsi kategori (opsional)"
              style={inputStyle} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Umur Ekonomis (bulan) *</label>
              <input type="number" value={form.economic_life_months} onChange={e => setForm({...form, economic_life_months: e.target.value})}
                placeholder="60"
                style={inputStyle} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Metode Depresiasi</label>
              <select value={form.depreciation_method} onChange={e => setForm({...form, depreciation_method: e.target.value})}
                style={inputStyle}>
                {depMethods.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Nilai Sisa (%)</label>
            <input type="number" value={form.residual_value_pct} onChange={e => setForm({...form, residual_value_pct: e.target.value})}
              placeholder="0"
              style={inputStyle} />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
            <Btn variant="secondary" onClick={closeModal}>Batal</Btn>
            <Btn onClick={handleSave} disabled={saving}>{saving ? "Menyimpan…" : "Simpan"}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
